import React from "react";
import PricingCard from "./components/shared/PricingCard";
import RateItem from "./components/shared/RateItem";
import { RegularCardIcons } from "./constants/icons";
import AmexIcon24 from "../../../../../../../../../../images/v2/amex24x24.svg";
import getPricingDataById from "./utils/get-pricing-data-by-id";
import formatCurrency from "./utils/format-currency";
import formatPercentage from "./utils/format-percentage";
import useSalesFormStore from "../../../../../../../../stores/sales-form";
import styles from "./style.module.scss";

const AMEX_TIERS = [
  {
    id: "one",
    title: "Amex Tier 1",
    rates: [
      { label: "AMEX Tier 1 Rate", id: 32 },
      { label: "AMEX Tier 1 Txn Fee", id: 32 },
    ],
  },
  {
    id: "two",
    title: "Amex Tier 2",
    rates: [
      { label: "AMEX Tier 2 Rate", id: 33 },
      { label: "AMEX Tier 2 Txn Fee", id: 33 },
    ],
  },
  {
    id: "three",
    title: "Amex Tier 3",
    rates: [
      { label: "AMEX Tier 3 Rate", id: 34 },
      { label: "AMEX Tier 3 Txn Fee", id: 34 },
    ],
  },
  {
    id: "four",
    title: "Amex Tier 4",
    rates: [
      { label: "AMEX Tier 4 Rate", id: 49 },
      { label: "AMEX Tier 4 Txn Fee", id: 49 },
    ],
  },
];

const AmexFlatRateCard = () => {
  const applicationData = useSalesFormStore((state) => state.applicationData);
  const amexRates = [
    {
      label: "Flat Rate",
      value: formatPercentage(
        getPricingDataById(35, applicationData?.prices)?.percent,
      ),
    },
    {
      label: "Txn Fee",
      value: formatCurrency(
        getPricingDataById(35, applicationData?.prices)?.dollars,
      ),
    },
    null,
    null,
  ];

  return (
    <div className={styles.amexCard}>
      <div className={styles.amexHeader}>
        <div className={styles.cardTitle}>Amex Flat Rate</div>
      </div>
      <div className={styles.ratesList}>
        {amexRates.map((rate, index) =>
          rate ? (
            <RateItem key={index} label={rate.label} value={rate.value} />
          ) : (
            <div key={index} className={styles.emptyRateItem} />
          ),
        )}
      </div>
    </div>
  );
};

const AmexTieredRateCard = () => {
  const applicationData = useSalesFormStore((state) => state.applicationData);
  return (
    <div className={styles.amexTieredCards}>
      {AMEX_TIERS.map((tier, index) => (
        <div key={index} className={styles.amexCard}>
          <div className={styles.amexHeader}>
            <div className={styles.cardTitle}>{tier.title}</div>
          </div>
          <div className={styles.ratesList}>
            {tier.rates.map((rate, rateIndex) => {
              const formatted = rate.label.includes("Rate")
                ? formatPercentage(
                    getPricingDataById(rate.id, applicationData?.prices)
                      ?.percent,
                  )
                : formatCurrency(
                    getPricingDataById(rate.id, applicationData?.prices)
                      ?.dollars,
                  );
              // Tier 2+ can be blank — show "-" instead of empty
              const value =
                tier.id !== "one" ? formatted || "-" : formatted;
              return (
                <RateItem key={rateIndex} label={rate.label} value={value} />
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

const AmexICPlusCard = () => {
  const applicationData = useSalesFormStore((state) => state.applicationData);
  const rates = [
    {
      label: "IC+ Rate",
      value: formatPercentage(
        getPricingDataById(35, applicationData?.prices)?.percent,
      ),
    },
    {
      label: "Txn Fee",
      value: formatCurrency(
        getPricingDataById(35, applicationData?.prices)?.dollars,
      ),
    },
  ];

  return (
    <div className={styles.amexCard}>
      <div className={styles.amexHeader}>
        <div className={styles.cardTitle}>Amex Interchange Rate</div>
      </div>
      <div className={styles.ratesList}>
        {rates.map((rate, index) => (
          <RateItem key={index} label={rate.label} value={rate.value} />
        ))}
      </div>
    </div>
  );
};

const AmexSection = () => {
  const applicationData = useSalesFormStore((state) => state.applicationData);
  const pricingModel = applicationData?.pricing_model;

  const renderAmexContent = () => {
    switch (pricingModel) {
      case "flat_rate":
        return <AmexFlatRateCard />;
      case "tiered":
        return <AmexTieredRateCard />;
      case "ic_plus":
        return <AmexICPlusCard />;
      default:
        return null;
    }
  };

  return (
    <div className={styles.amexSection}>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionTitle}>AMEX STRUCTURE</div>
        <div className={styles.sectionLine} />
        <div className={styles.sectionIcon}>
          <div className={`${styles.cardIcon} ${styles.amex}`}>
            <img src={AmexIcon24} alt="AMEX" />
          </div>
        </div>
      </div>
      <div className={styles.amexDetails}>
        <RateItem
          label="Preferred AMEX Structure"
          value={
            pricingModel === "tiered"
              ? "Tiered Rate"
              : pricingModel === "ic_plus"
                ? "IC+"
                : "Flat Rate"
          }
        />
      </div>
      {renderAmexContent()}
    </div>
  );
};

const TieredRate = () => {
  const applicationData = useSalesFormStore((state) => state.applicationData);

  const qualifiedRates = [
    {
      label: "Qual rate",
      value: formatPercentage(
        getPricingDataById(1, applicationData?.prices)?.percent,
      ),
    },
    {
      label: "Qual Txn Fee",
      value: formatCurrency(
        getPricingDataById(1, applicationData?.prices)?.dollars,
      ),
    },
    {
      label: "Qual Debit Rate",
      value: formatPercentage(
        getPricingDataById(39, applicationData?.prices)?.percent,
      ),
    },
    {
      label: "Qual Debit Txn Fee",
      value: formatCurrency(
        getPricingDataById(39, applicationData?.prices)?.dollars,
      ),
    },
  ];

  const midQualifiedRates = [
    {
      label: "Mid-Qual Rate",
      value:
        formatPercentage(
          getPricingDataById(3, applicationData?.prices)?.percent,
        ) || "-",
    },
    {
      label: "Mid-Qual Txn Fee",
      value:
        formatCurrency(
          getPricingDataById(3, applicationData?.prices)?.dollars,
        ) || "-",
    },
    {
      label: "Mid-Qual Debit Rate",
      value:
        formatPercentage(
          getPricingDataById(40, applicationData?.prices)?.percent,
        ) || "-",
    },
    {
      label: "Mid-Qual Debit Txn Fee",
      value:
        formatCurrency(
          getPricingDataById(40, applicationData?.prices)?.dollars,
        ) || "-",
    },
  ];

  const nonQualifiedRates = [
    {
      label: "Non-Qual Rate",
      value:
        formatPercentage(
          getPricingDataById(4, applicationData?.prices)?.percent,
        ) || "-",
    },
    {
      label: "Non-Qual Txn Fee",
      value:
        formatCurrency(
          getPricingDataById(4, applicationData?.prices)?.dollars,
        ) || "-",
    },
    {
      label: "Non-Qual Debit Rate",
      value:
        formatPercentage(
          getPricingDataById(41, applicationData?.prices)?.percent,
        ) || "-",
    },
    {
      label: "Non-Qual Debit Txn Fee",
      value:
        formatCurrency(
          getPricingDataById(41, applicationData?.prices)?.dollars,
        ) || "-",
    },
  ];

  return (
    <div className={styles.tieredRate}>
      <div className={styles.rateCards}>
        <PricingCard title="Qualified" icons={<RegularCardIcons />}>
          <div className={styles.ratesList}>
            {qualifiedRates.map((rate, index) => (
              <RateItem key={index} label={rate.label} value={rate.value} />
            ))}
          </div>
        </PricingCard>

        <PricingCard title="Mid-Qualified" icons={<RegularCardIcons />}>
          <div className={styles.ratesList}>
            {midQualifiedRates.map((rate, index) => (
              <RateItem key={index} label={rate.label} value={rate.value} />
            ))}
          </div>
        </PricingCard>

        <PricingCard title="Non-Qualified" icons={<RegularCardIcons />}>
          <div className={styles.ratesList}>
            {nonQualifiedRates.map((rate, index) => (
              <RateItem key={index} label={rate.label} value={rate.value} />
            ))}
          </div>
        </PricingCard>

        {applicationData?.pricing_model && <AmexSection />}
      </div>
    </div>
  );
};

export default TieredRate;
